import axios, { type AxiosInstance } from 'axios';
import type { AxiosError } from 'axios';
import { userManager } from '@/config/keycloak';

// Get config from runtime window object (loaded from config.js)
const appConfig = (window as any).__APP_CONFIG__ || {};
const API_BASE_URL = appConfig.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Default timeout: 5 minutes (300000 ms) for regular requests
  timeout: 300000,
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get user from userManager (most reliable method)
      const user = await userManager.getUser();

      if (user?.access_token) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
      }
    } catch (error) {
      // Fallback: try to get token from storage directly
      console.warn('Failed to get user from userManager, trying storage fallback:', error);

      const authority = appConfig.VITE_AUTHORITY || import.meta.env.VITE_AUTHORITY;
      const clientId = appConfig.VITE_CLIENT_ID || import.meta.env.VITE_CLIENT_ID;
      const storageKey = `oidc.user:${authority}:${clientId}`;

      // Try localStorage first (as configured in keycloak.ts)
      let oidcStorage = localStorage.getItem(storageKey);

      // Fallback to sessionStorage if not found in localStorage
      if (!oidcStorage) {
        oidcStorage = sessionStorage.getItem(storageKey);
      }

      if (oidcStorage) {
        try {
          const user = JSON.parse(oidcStorage);
          if (user?.access_token) {
            config.headers.Authorization = `Bearer ${user.access_token}`;
          }
        } catch (parseError) {
          console.error('Failed to parse OIDC storage:', parseError);
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - error logging
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail: string }>) => {
    // Check if it's a timeout error
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      const message = 'Request timeout - the operation took too long. Please try again.';
      console.error('API Timeout Error:', {
        message,
        url: error.config?.url,
        timeout: error.config?.timeout,
      });
      return Promise.reject(new Error(message));
    }

    const message = error.response?.data?.detail || error.message || 'Unexpected error';

    // Log error for debugging
    console.error('API Error:', {
      message,
      status: error.response?.status,
      url: error.config?.url,
    });

    // Re-throw for component-level handling
    return Promise.reject(error);
  }
);
